import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  Chip,
  Button,
  List,
  ListItem,
  ListItemText,
  Divider,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper
} from '@mui/material';
import {
  Security as SecurityIcon,
  Shield,
  Warning,
  Refresh,
  Verified,
  GppBad,
  GppGood
} from '@mui/icons-material';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import adminApi from '../services/api';

interface SecurityData {
  systemStats: any;
  attestationAnalytics: any;
  securityInsights: any;
}

const Security: React.FC = () => {
  const [data, setData] = useState<SecurityData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadSecurityData();
  }, []);

  const loadSecurityData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const [systemStats, attestationAnalytics, securityInsights] = await Promise.all([
        adminApi.getSystemStats(),
        adminApi.getAttestationAnalytics({}),
        adminApi.getSecurityInsights().catch(() => ({ insights: {} })) // Fallback if analytics not available
      ]);

      setData({
        systemStats,
        attestationAnalytics,
        securityInsights: securityInsights.insights || {}
      });
    } catch (err: any) {
      const errorMessage = err?.response?.data?.message || err?.message || 'Failed to load security data';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const getSecurityLevelColor = (level: string) => {
    switch (level) {
      case 'STRONGBOX': return '#4caf50';
      case 'TEE': return '#ff9800';
      case 'SOFTWARE': return '#f44336';
      default: return '#757575';
    }
  };

  const getThreatLevelColor = (level: string) => {
    switch (level) {
      case 'low': return 'success';
      case 'medium': return 'warning';
      case 'high': return 'error';
      case 'critical': return 'error';
      default: return 'default';
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {error}
        <Button onClick={loadSecurityData} sx={{ ml: 2 }}>
          Retry
        </Button>
      </Alert>
    );
  }

  if (!data) return null;

  const { systemStats, attestationAnalytics, securityInsights } = data;

  // Prepare chart data
  const securityLevelData = Object.entries(attestationAnalytics.security_levels || {}).map(([level, count]) => ({
    name: level,
    value: count as number,
    color: getSecurityLevelColor(level)
  }));

  const errorCodeData = Object.entries(attestationAnalytics.error_codes || {}).map(([code, count]) => ({
    code,
    count: count as number
  }));

  return (
    <Box sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 4 }}>
        <Typography variant="h4" fontWeight="bold">
          🔒 Security Center
        </Typography>
        <Button
          variant="outlined"
          startIcon={<Refresh />}
          onClick={loadSecurityData}
        >
          Refresh
        </Button>
      </Box>

      {/* Security Overview Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography variant="h6" gutterBottom>Threat Level</Typography>
                  <Typography variant="h4" fontWeight="bold" sx={{ textTransform: 'uppercase' }}>
                    {securityInsights.threat_level || 'Low'}
                  </Typography>
                </Box>
                <SecurityIcon sx={{ fontSize: 48, opacity: 0.8 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)', color: 'white' }}>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography variant="h6" gutterBottom>Attestation Rate</Typography>
                  <Typography variant="h4" fontWeight="bold">
                    {(systemStats.attestation_success_rate_24h * 100).toFixed(1)}%
                  </Typography>
                </Box>
                <Verified sx={{ fontSize: 48, opacity: 0.8 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', color: 'white' }}>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography variant="h6" gutterBottom>StrongBox Devices</Typography>
                  <Typography variant="h4" fontWeight="bold">{systemStats.strongbox_devices}</Typography>
                </Box>
                <Shield sx={{ fontSize: 48, opacity: 0.8 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)', color: 'white' }}>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography variant="h6" gutterBottom>Active Rate Limits</Typography>
                  <Typography variant="h4" fontWeight="bold">{systemStats.active_rate_limits}</Typography>
                </Box>
                <Warning sx={{ fontSize: 48, opacity: 0.8 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Attestation Analytics */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Attestation Success Rate</Typography>
              <Box sx={{ mb: 2 }}>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Typography variant="body2">Successful Attestations</Typography>
                  <Typography variant="body2" fontWeight="bold">
                    {attestationAnalytics.successful_attestations || 0}
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={Math.min(100, ((attestationAnalytics.successful_attestations || 0) / Math.max(1, attestationAnalytics.total_attestation_attempts || 1)) * 100)}
                  sx={{ height: 8, borderRadius: 4, backgroundColor: 'rgba(76, 175, 80, 0.2)' }}
                />
              </Box>
              <Box sx={{ mb: 2 }}>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Typography variant="body2">Failed Attestations</Typography>
                  <Typography variant="body2" fontWeight="bold">
                    {attestationAnalytics.failed_attestations || 0}
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={Math.min(100, ((attestationAnalytics.failed_attestations || 0) / Math.max(1, attestationAnalytics.total_attestation_attempts || 1)) * 100)}
                  sx={{ 
                    height: 8, 
                    borderRadius: 4, 
                    backgroundColor: 'rgba(244, 67, 54, 0.2)',
                    '& .MuiLinearProgress-bar': { backgroundColor: '#f44336' }
                  }}
                />
              </Box>
              <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mt: 2 }}>
                <Typography variant="body2" color="text.secondary">Total Attempts:</Typography>
                <Typography variant="h6">{attestationAnalytics.total_attestation_attempts || 0}</Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Security Level Distribution</Typography>
              {securityLevelData.length > 0 ? (
                <ResponsiveContainer width="100%" height={200}>
                  <PieChart>
                    <Pie
                      data={securityLevelData}
                      cx="50%"
                      cy="50%"
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                      label={({ name, value }) => `${name}: ${value}`}
                    >
                      {securityLevelData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                  No attestation data available
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Security Metrics */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Security Metrics</Typography>
              <Grid container spacing={2}>
                <Grid item xs={6} md={3}>
                  <Box textAlign="center">
                    <Typography variant="h4" fontWeight="bold" color="success.main">
                      {attestationAnalytics.devices_with_attestation || 0}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Devices with Attestation
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Box textAlign="center">
                    <Typography variant="h4" fontWeight="bold" color="error.main">
                      {attestationAnalytics.devices_without_attestation || 0}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Devices without Attestation
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Box textAlign="center">
                    <Typography variant="h4" fontWeight="bold" color="primary.main">
                      {((attestationAnalytics.strongbox_adoption_rate || 0) * 100).toFixed(1)}%
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      StrongBox Adoption
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Box textAlign="center">
                    <Typography variant="h4" fontWeight="bold" color="warning.main">
                      {Object.keys(attestationAnalytics.error_codes || {}).length}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Error Types
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Security Status</Typography>
              <List>
                <ListItem>
                  <ListItemText
                    primary={
                      <Box display="flex" alignItems="center" gap={1}>
                        {systemStats.database_healthy ? <GppGood color="success" /> : <GppBad color="error" />}
                        <Typography>Database Security</Typography>
                      </Box>
                    }
                    secondary={systemStats.database_healthy ? 'Secure' : 'Issues detected'}
                  />
                </ListItem>
                <Divider />
                <ListItem>
                  <ListItemText
                    primary={
                      <Box display="flex" alignItems="center" gap={1}>
                        {systemStats.redis_healthy ? <GppGood color="success" /> : <GppBad color="error" />}
                        <Typography>Redis Security</Typography>
                      </Box>
                    }
                    secondary={systemStats.redis_healthy ? 'Secure' : 'Issues detected'}
                  />
                </ListItem>
                <Divider />
                <ListItem>
                  <ListItemText
                    primary={
                      <Box display="flex" alignItems="center" gap={1}>
                        <Chip
                          label={securityInsights.threat_level?.toUpperCase() || 'LOW'}
                          color={getThreatLevelColor(securityInsights.threat_level)}
                          size="small"
                        />
                        <Typography>Threat Level</Typography>
                      </Box>
                    }
                    secondary="Overall system threat assessment"
                  />
                </ListItem>
              </List>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Error Analysis */}
      {errorCodeData.length > 0 && (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Attestation Error Analysis</Typography>
                <TableContainer component={Paper} sx={{ mt: 2 }}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Error Code</TableCell>
                        <TableCell>Count</TableCell>
                        <TableCell>Percentage</TableCell>
                        <TableCell>Severity</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {errorCodeData.map((error) => {
                        const percentage = ((error.count / (attestationAnalytics.total_attestation_attempts || 1)) * 100).toFixed(2);
                        const severity = error.count > 10 ? 'high' : error.count > 5 ? 'medium' : 'low';
                        
                        return (
                          <TableRow key={error.code}>
                            <TableCell>
                              <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                                {error.code}
                              </Typography>
                            </TableCell>
                            <TableCell>{error.count}</TableCell>
                            <TableCell>{percentage}%</TableCell>
                            <TableCell>
                              <Chip
                                label={severity.toUpperCase()}
                                color={severity === 'high' ? 'error' : severity === 'medium' ? 'warning' : 'success'}
                                size="small"
                              />
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Security Recommendations */}
      {securityInsights.recommendations && securityInsights.recommendations.length > 0 && (
        <Card sx={{ mt: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>Security Recommendations</Typography>
            <List>
              {securityInsights.recommendations.map((recommendation: string, index: number) => (
                <ListItem key={index}>
                  <ListItemText
                    primary={
                      <Box display="flex" alignItems="flex-start" gap={1}>
                        <Typography variant="body2" sx={{ color: 'primary.main' }}>
                          💡
                        </Typography>
                        <Typography variant="body2">
                          {recommendation}
                        </Typography>
                      </Box>
                    }
                  />
                </ListItem>
              ))}
            </List>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default Security; 